/**
 * flight-path.ts — Ruta simulada de un vuelo de fumigación (para la repetición en la página de drones).
 *
 * El registro del vuelo no trae la trayectoria, solo superficie y separación entre pasadas; aquí se
 * dibuja un barrido en "S" centrado en el lote, con la superficie tratada y la separación reales.
 */

export type LatLng = [number, number];

const M_PER_DEG_LAT = 111_320;

/** Barrido en S de `areaHa` hectáreas dentro del rectángulo envolvente del anillo del lote. */
export function buildFlightPath(ring: LatLng[], areaHa: number, rowSpacingM = 7): LatLng[] {
  if (ring.length < 3 || !(areaHa > 0)) return [];
  const lats = ring.map(p => p[0]);
  const lngs = ring.map(p => p[1]);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const cLat = (minLat + maxLat) / 2, cLng = (minLng + maxLng) / 2;
  const mPerDegLng = M_PER_DEG_LAT * Math.cos((cLat * Math.PI) / 180);
  const W = (maxLng - minLng) * mPerDegLng;
  const H = (maxLat - minLat) * M_PER_DEG_LAT;
  if (W <= 0 || H <= 0) return [];

  // Rectángulo tratado: misma proporción que el lote, con la superficie del vuelo (sin salirse del lote).
  const target = areaHa * 10_000;
  const scale = Math.min(1, Math.sqrt(target / (W * H)));
  const rw = W * scale, rh = H * scale;
  const lanes = Math.max(2, Math.ceil(rh / Math.max(rowSpacingM, 1)) + 1);
  const pts: LatLng[] = [];
  for (let i = 0; i < lanes; i++) {
    const y = -rh / 2 + (i * rh) / (lanes - 1);
    const xs = i % 2 === 0 ? [-rw / 2, rw / 2] : [rw / 2, -rw / 2];
    for (const x of xs) pts.push([cLat + y / M_PER_DEG_LAT, cLng + x / mPerDegLng]);
  }
  return pts;
}

/** Longitud acumulada (en grados, suficiente para repartir el avance) en cada vértice. */
function cumulative(points: LatLng[]): number[] {
  const acc = [0];
  for (let i = 1; i < points.length; i++) {
    acc.push(acc[i - 1] + Math.hypot(points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1]));
  }
  return acc;
}

/** Tramo recorrido hasta `progress` (0..1) y posición actual del dron. */
export function flightAt(points: LatLng[], progress: number): { done: LatLng[]; position: LatLng | null } {
  if (points.length === 0) return { done: [], position: null };
  const p = Math.min(1, Math.max(0, progress));
  const acc = cumulative(points);
  const total = acc[acc.length - 1];
  if (total === 0) return { done: [points[0]], position: points[0] };
  const target = p * total;
  let i = 1;
  while (i < points.length - 1 && acc[i] < target) i++;
  const seg = acc[i] - acc[i - 1] || 1;
  const f = Math.min(1, Math.max(0, (target - acc[i - 1]) / seg));
  const a = points[i - 1], b = points[i];
  const position: LatLng = [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
  return { done: [...points.slice(0, i), position], position };
}
